import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initials = (name = '') =>
  name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const timeAgo = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export const FlexCommentsBoard = ({ systemId, systemName, accent }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/flex-detox/comments/${systemId}`);
      const data = await res.json();
      setComments(data.comments || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const body = text.trim();
    if (!body || posting) return;
    setPosting(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/flex-detox/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system_id: systemId,
          text: body,
          user_email: user?.email,
          user_name: user?.name || 'Client',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not post comment');
      setComments((prev) => [...prev, data.comment]);
      setText('');
    } catch (e) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="bg-white/70 rounded-xl p-4" data-testid={`comments-board-${systemId}`}>
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle size={16} className="text-forest" />
        <h4 className="font-heading text-lg text-forest">Comments</h4>
        <span className="text-xs font-body text-stone-400">· {systemName} check-in</span>
        {comments.length > 0 && (
          <span className="ml-auto text-xs font-body text-stone-400">{comments.length}</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-stone-400 text-sm py-3">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm font-body text-stone-400 italic mb-3">
          Be the first to share how these three days are going. 🌿
        </p>
      ) : (
        <ul className="space-y-3 mb-4">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-3 group" data-testid={`comment-${c.id}`}>
              <div className="w-8 h-8 rounded-full bg-forest/10 text-forest flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {initials(c.user_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-body text-sm font-medium text-forest">{c.user_name}</span>
                  <span className="text-[11px] font-body text-stone-400">{timeAgo(c.created_at)}</span>
                </div>
                <p className="font-body text-sm text-stone-700 whitespace-pre-wrap break-words">{c.text}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Share a thought about ${systemName}…`}
          rows={2}
          className="flex-1 text-sm font-body text-stone-700 bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta resize-none placeholder:text-stone-400"
          data-testid={`comment-input-${systemId}`}
        />
        <button
          onClick={post}
          disabled={posting || !text.trim()}
          className="flex-shrink-0 inline-flex items-center gap-1.5 bg-forest text-cream text-sm font-body font-medium px-3.5 py-2 rounded-lg hover:bg-forest-dark transition-colors disabled:opacity-40"
          data-testid={`comment-post-${systemId}`}
        >
          {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Post
        </button>
      </div>
      {error && <p className="text-xs font-body text-red-500 mt-2" data-testid="comment-error">{error}</p>}
    </div>
  );
};

export default FlexCommentsBoard;
